/**
 * Clients API
 * Handles all client-related API calls
 */
import apiClient from './apiClient';

export interface Client {
  id: number;
  name: string;
  code: string | null;
  description: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  is_active: boolean;
  created_by: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface ClientsListResponse {
  clients: Client[];
  total: number;
}

export interface ClientsQueryParams {
  include_inactive?: boolean;
  is_active?: string;  // 'true', 'false', or empty for all
  search?: string;
}

export interface CreateClientData {
  name: string;
  code?: string;
  description?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
}

export interface UpdateClientData {
  name?: string;
  code?: string;
  description?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  is_active?: boolean;
}

export const clientsApi = {
  /**
   * Get all clients
   */
  getClients: async (params?: ClientsQueryParams): Promise<ClientsListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.include_inactive) queryParams.append('include_inactive', 'true');
    if (params?.is_active !== undefined && params?.is_active !== '') {
      queryParams.append('is_active', params.is_active);
    }
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    return apiClient.get<ClientsListResponse>(`/clients${query ? `?${query}` : ''}`);
  },

  /**
   * Get a specific client by ID
   */
  getClient: async (clientId: number): Promise<{ client: Client }> => {
    return apiClient.get<{ client: Client }>(`/clients/${clientId}`);
  },

  /**
   * Create a new client
   */
  createClient: async (data: CreateClientData): Promise<{ message: string; client: Client }> => {
    return apiClient.post<{ message: string; client: Client }>('/clients', data);
  },

  /**
   * Update an existing client
   */
  updateClient: async (
    clientId: number,
    data: UpdateClientData
  ): Promise<{ message: string; client: Client }> => {
    return apiClient.put<{ message: string; client: Client }>(`/clients/${clientId}`, data);
  },

  /**
   * Delete (soft delete) a client
   */
  deleteClient: async (clientId: number): Promise<{ message: string }> => {
    return apiClient.delete<{ message: string }>(`/clients/${clientId}`);
  },

  /**
   * Get projects for a specific client
   */
  getClientProjects: async (clientId: number): Promise<{
    client_id: number;
    client_name: string;
    projects: Array<{
      id: number;
      name: string;
      code: string | null;
      status: string;
    }>;
    total: number;
  }> => {
    return apiClient.get(`/clients/${clientId}/projects`);
  },

  /**
   * Export clients to CSV or XLSX
   */
  exportClients: async (params?: {
    format?: 'csv' | 'xlsx';
    include_inactive?: boolean;
    is_active?: string;
    search?: string;
    columns?: string;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    if (params?.format) queryParams.append('format', params.format);
    if (params?.include_inactive) queryParams.append('include_inactive', 'true');
    if (params?.is_active !== undefined && params?.is_active !== '') {
      queryParams.append('is_active', params.is_active);
    }
    if (params?.search) queryParams.append('search', params.search);
    if (params?.columns) queryParams.append('columns', params.columns);
    const query = queryParams.toString();
    return apiClient.getBlob(`/clients/export${query ? `?${query}` : ''}`);
  },
};

export default clientsApi;
